import React, { useState, useRef } from 'react';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import Fade from '@mui/material/Fade';
import AppsIcon from '@mui/icons-material/Apps';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import BorderColorRoundedIcon from '@mui/icons-material/BorderColorRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import styled from 'styled-components';

import QI_APPS from './apps-config';

const ICON_MAP = {
  security: <SecurityRoundedIcon />,
  assignment: <AssignmentTurnedInRoundedIcon />,
  edit: <BorderColorRoundedIcon />,
  account_balance: <AccountBalanceRoundedIcon />,
};

const Trigger = styled(IconButton)`
  && {
    color: #0b1f82;
    padding: 10px;
    transition: all 0.2s ease;
    & svg {
      font-size: 28px;
    }
    &:hover {
      background-color: rgba(11, 31, 130, 0.08);
      transform: scale(1.1);
    }
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
`;

const HeaderTitle = styled(Typography)`
  && {
    font-size: 15px;
    font-weight: 600;
    color: #334150;
    letter-spacing: -0.01em;
  }
`;

const HeaderBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: #0b1f82;
  background-color: #e6eaff;
  padding: 2px 8px;
  border-radius: 10px;
  letter-spacing: 0.03em;
  text-transform: uppercase;
`;

const StyledDivider = styled.div`
  height: 1px;
  margin: 0 20px;
  background: linear-gradient(90deg, transparent, #d8dce8, transparent);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  padding: 16px 12px 20px;
`;

const AppCard = styled.a`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 16px 8px 12px;
  border-radius: 14px;
  text-decoration: none;
  color: #334150;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  border: none;
  background-color: ${({ $isCurrent }) =>
    $isCurrent ? '#f2f4fc' : 'transparent'};

  &:hover {
    background-color: #f2f4fc;
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus {
    outline: 2px solid #0b1f82;
    outline-offset: -2px;
    border-radius: 14px;
  }
`;

const AppIconWrapper = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 16px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  color: #fff;

  & svg {
    font-size: 26px;
  }

  ${AppCard}:hover & {
    transform: scale(1.08);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  }
`;

const AppName = styled.span`
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  line-height: 1.3;
  word-break: break-word;
  max-width: 90px;
  color: #334150;
`;

const CurrentCheck = styled.span`
  position: absolute;
  top: -3px;
  right: -3px;
  width: 18px;
  height: 18px;
  color: #fff;
  background-color: #0b1f82;
  border-radius: 50%;
  border: 2px solid #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(11, 31, 130, 0.4);

  & svg {
    font-size: 12px;
  }
`;

export default function AppSwitcher() {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const handleToggle = () => setOpen((prev) => !prev);
  const handleClose = () => setOpen(false);

  const currentUrl = window.location.origin;

  return (
    <>
      <Trigger
        ref={anchorRef}
        onClick={handleToggle}
        aria-label="App switcher"
        aria-haspopup="true"
        aria-expanded={open}
      >
        <AppsIcon />
      </Trigger>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 200 }}
        sx={{
          marginTop: '8px',
        }}
        PaperProps={{
          sx: {
            width: 340,
            maxHeight: 440,
            overflowY: 'auto',
            borderRadius: '16px',
            boxShadow:
              '0 12px 48px rgba(11, 31, 130, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(11, 31, 130, 0.06)',
          },
        }}
      >
        <Header>
          <HeaderTitle>Aplicativos QI Tech</HeaderTitle>
          <HeaderBadge>{QI_APPS.length} apps</HeaderBadge>
        </Header>
        <StyledDivider />
        <Grid>
          {QI_APPS.map((app) => {
            const isCurrent = currentUrl === app.url.replace(/\/$/, '');
            return (
              <AppCard
                key={app.name}
                href={app.url}
                $isCurrent={isCurrent}
                title={app.description}
                onClick={handleClose}
                rel="noopener noreferrer"
              >
                <AppIconWrapper
                  style={{
                    background: `linear-gradient(135deg, ${app.color.from}, ${app.color.to})`,
                  }}
                >
                  {ICON_MAP[app.iconName]}
                  {isCurrent && (
                    <CurrentCheck>
                      <CheckCircleIcon />
                    </CurrentCheck>
                  )}
                </AppIconWrapper>
                <AppName>{app.name}</AppName>
              </AppCard>
            );
          })}
        </Grid>
      </Popover>
    </>
  );
}
